import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Mail, ShieldCheck } from 'lucide-react';

import { requestLoginCode, verifyLoginCode } from '@/services/auth';
import { useAuthStore } from '@/store/auth.store';
import { useEstablishmentStore } from '@/store/establishment.store';
import logoWide from '@/images/Resstologo2.png';

type Step = 'email' | 'code';

export default function Login() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // cooldown до следующей возможной отправки кода (в секундах)
  const [cooldown, setCooldown] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const setEstablishmentId = useEstablishmentStore((s) => s.setCurrentId);
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  // Тикер cooldown'а.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Когда переходим на шаг ввода кода — фокус на поле.
  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  const onRequestCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Введите email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Некорректный email');
      return;
    }
    try {
      setSubmitting(true);
      const res = await requestLoginCode(trimmed);
      const remaining = Math.max(0, Math.round((new Date(res.nextAt).getTime() - Date.now()) / 1000));
      setCooldown(remaining || 60);
      setStep('code');
      toast.success('Код отправлен на почту');
    } catch {
      // тост уже выведен интерсептором axios
    } finally {
      setSubmitting(false);
    }
  };

  const onVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = code.replace(/\s+/g, '');
    if (!/^\d{4,8}$/.test(trimmed)) {
      toast.error('Введите код из письма');
      return;
    }
    try {
      setSubmitting(true);
      const res = await verifyLoginCode(email.trim(), trimmed);
      setSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        user: res.user,
      });
      setEstablishmentId(res.user.establishmentId ?? null);
      toast.success('Добро пожаловать');
      const to = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(to, { replace: true });
    } catch {
      // тост уже выведен интерсептором
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    if (cooldown > 0 || submitting) return;
    await onRequestCode();
  };

  const onChangeEmail = () => {
    setStep('email');
    setCode('');
  };

  return (
    <div className="min-h-full flex items-center justify-center p-4">
      <div className="w-full max-w-md card p-7">
        <div className="flex flex-col items-center text-center mb-6">
          <img
            src={logoWide}
            alt="Resstro"
            className="h-14 w-auto object-contain mb-3"
          />
          <div className="text-sm text-slate-500">
            {step === 'email' ? 'Вход в админ-панель' : 'Подтверждение по почте'}
          </div>
        </div>

        {step === 'email' && (
          <form className="space-y-4" onSubmit={onRequestCode}>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                placeholder="you@yourcafe.ru"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="email"
                inputMode="email"
              />
              <div className="mt-2 text-xs text-slate-500">
                На указанную почту придёт код. Для владельцев и администраторов заведения.
              </div>
            </div>

            <button
              className="btn-primary w-full"
              type="submit"
              disabled={submitting}
            >
              <Mail size={16} /> {submitting ? 'Отправляем…' : 'Получить код'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form className="space-y-4" onSubmit={onVerify}>
            <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
              <span className="truncate">
                Код отправлен на <span className="font-medium text-slate-700">{email}</span>
              </span>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-orange-600 hover:underline"
                onClick={onChangeEmail}
              >
                <ArrowLeft size={12} /> сменить
              </button>
            </div>

            <div>
              <label className="label">Код из письма</label>
              <input
                ref={codeInputRef}
                type="text"
                className="input tracking-[0.4em] text-center text-lg font-mono"
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={8}
              />
            </div>

            <button
              className="btn-primary w-full"
              type="submit"
              disabled={submitting || code.length < 4}
            >
              <ShieldCheck size={16} /> {submitting ? 'Проверяем…' : 'Войти'}
            </button>

            <div className="text-center text-xs text-slate-500">
              {cooldown > 0 ? (
                <>Отправить новый код можно через {cooldown} сек.</>
              ) : (
                <button
                  type="button"
                  className="text-orange-600 hover:underline"
                  onClick={onResend}
                  disabled={submitting}
                >
                  Отправить код повторно
                </button>
              )}
            </div>
          </form>
        )}

        <div className="mt-5 text-xs text-slate-400 text-center">
          Нет аккаунта? Зарегистрируйтесь на{' '}
          <a href="https://resstro.ru" className="text-orange-600 hover:underline">
            resstro.ru
          </a>
        </div>
      </div>
    </div>
  );
}
